// src/pages/ProfileSubscription.tsx

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Calendar, CreditCard, ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { useHeaderContext } from '../contexts/HeaderContext';
import SubscriptionStatusCard from '../components/profile/SubscriptionStatusCard';

const ProfileSubscription: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { subscription, display, isLoading } = useSubscription(currentUser);
  const { setConfig } = useHeaderContext();

  const isPro = subscription?.status === 'active';

  // 設定 Header
  useEffect(() => {
    setConfig({
      title: 'Manage Subscription',
      leftAction: (
        <button
          onClick={() => navigate('/profile')}
          className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Back to Profile"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
      )
    });

    return () => setConfig(null);
  }, [setConfig, navigate]);

  // 如果是免費用戶，重定向到方案選擇頁面
  useEffect(() => {
    if (!isLoading && !isPro) {
      navigate('/plans', { replace: true });
    }
  }, [isLoading, isPro, navigate]);

  // 載入中或免費用戶（正在重定向）時顯示載入狀態
  if (isLoading || !isPro) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
        {/* Current Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <SubscriptionStatusCard
            display={display}
            loading={false}
          />
        </div>

        {/* Plan Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Crown className="w-5 h-5 text-yellow-500 mr-2" />
              <h3 className="font-semibold text-gray-900">Pro Plan</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-gray-700">Billing Cycle</span>
                </div>
                <span className="font-medium text-gray-900">
                  {subscription.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </span>
              </div>

              {subscription.expiredDate && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCard className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-700">Valid Until</span>
                  </div>
                  <span className="font-medium text-gray-900">
                    {new Date(subscription.expiredDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Features Included */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Features Included</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <Check size={16} className="text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Unlimited smart task planning</span>
              </li>
              <li className="flex items-center">
                <Check size={16} className="text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Advanced insights</span>
              </li>
              <li className="flex items-center">
                <Check size={16} className="text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Smart task conversion</span>
              </li>
              <li className="flex items-center">
                <Check size={16} className="text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-700">Priority support</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Billing Cycle Management - 暫時註解，等產品策略確定後再啟用 */}
        {/*
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Billing Options</h3>
          <p className="text-gray-600 text-sm mb-4">
            Switch between monthly and yearly billing. Annual subscriptions save 33%.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                subscription.billingCycle === 'monthly'
                  ? 'border-sky-500 bg-sky-50 text-sky-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              disabled={subscription.billingCycle === 'monthly'}
            >
              <div className="text-left">
                <div className="font-semibold">Monthly</div>
                <div className="text-sm text-gray-600">$12/month</div>
              </div>
            </button>
            <button
              className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                subscription.billingCycle === 'yearly'
                  ? 'border-sky-500 bg-sky-50 text-sky-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              disabled={subscription.billingCycle === 'yearly'}
            >
              <div className="text-left">
                <div className="font-semibold">Yearly <span className="text-green-600 text-sm">Save 33%</span></div>
                <div className="text-sm text-gray-600">$8/month</div>
              </div>
            </button>
          </div>
        </div>
        */}

        {/* Manage Subscription */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Manage Subscription</h3>
          <p className="text-gray-600 text-sm mb-4">
            For subscription changes including billing cycle adjustments, payment method updates, or cancellation,
            please contact our support team. We're here to help with any subscription management needs.
          </p>
          <div className="flex justify-center">
            <a
              href="mailto:support@orlo.cc"
              className="inline-flex justify-center py-3 px-6 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
          </div>

          {/* 取消訂閱按鈕 - 暫時註解，透過客服處理 */}
          {/*
          <button className="flex-1 inline-flex justify-center py-3 px-4 border border-red-300 text-red-700 hover:bg-red-50 rounded-md shadow-sm text-sm font-medium transition-colors">
            <AlertTriangle size={16} className="mr-2" />
            Cancel Subscription
          </button>
          */}
        </div>
    </div>
  );
};

export default ProfileSubscription;